import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

function omit<T>(key: keyof T, obj: T) {
  const { [key]: ommitted, ...rest } = obj;
  return rest;
}

function add<T, D>(item: D, obj: T) {
  return {
    ...obj,
    ...item,
  };
}

export function SelectItemsProvider({ children }: { children: ReactNode }) {
  return;
}

type ProviderType = {
  (props: any): JSX.Element;
  api?: any;
};

const SelectItemsContext = createContext<ReturnType<typeof useSelectItems>>(
  {} as any,
);
SelectItemsContext.displayName = 'SelectItemsContext';

export const useSelectItemsContext = () => useContext(SelectItemsContext);

type OptionsType<Item> = {
  canSelect?: (item: Item) => boolean;
  itemId?: (item: Item) => string | number;
  initial?: Item[];
};
export function useSelectItems<Item>(
  items?: Item[],
  _options?: OptionsType<Item>,
) {
  const options = {
    initial: _options?.initial || [],
    canSelect: _options?.canSelect ? _options.canSelect : () => true,
    // @ts-ignore
    getItemKey: _options?.itemId ? _options.itemId : (i: Item) => i.id,
  };
  const { canSelect, getItemKey, initial } = options;

  const [selected, setSelected] = useState<{ [id: number | string]: Item }>(
    () =>
      initial.reduce((all, cur) => add({ [getItemKey(cur)]: cur }, all), {}),
  );
  const selectableItems = items?.filter(canSelect) || [];

  const unSelectCurrentPage = () => {
    setSelected((state) => {
      return (items || []).reduce(
        (all, cur) => omit(getItemKey(cur), all),
        state,
      );
    });
  };
  const selectCurrentPage = () => {
    setSelected((state) => {
      return selectableItems.reduce(
        (all, cur) => add({ [getItemKey(cur)]: cur }, all),
        state,
      );
    });
  };
  const unselectAll = () => {
    setSelected((state) => ({}));
  };

  const selectItem = (d: Item) => {
    if (canSelect(d)) {
      setSelected((state) => add({ [getItemKey(d)]: d }, state));
    }
  };

  const unselectItem = (d: Item) => {
    setSelected((state) => omit(getItemKey(d), state));
  };

  const toggleSelectItem = (d: Item) => {
    const isSelected = !!selected[getItemKey(d)];

    isSelected ? unselectItem(d) : selectItem(d);
  };

  const isAllSelected =
    selectableItems.length > 0
      ? selectableItems.every((node) => !!selected[getItemKey(node)])
      : false;

  const selectedCount = Object.values(selected).length;

  const selectUnselectPage = () => {
    isAllSelected ? unSelectCurrentPage() : selectCurrentPage();
  };

  const selectOne = (item: Item) => {
    unselectAll();
    selectItem(item);
  };

  const toggleSelectOne = (item: Item) => {
    const isSelected = !!selected[getItemKey(item)];
    unselectAll();
    isSelected ? unselectItem(item) : selectItem(item);
  };

  const API = {
    selected,
    toggleSelectOne,
    setSelected,
    selectOne,
    selectableItems,
    selectCurrentPage,
    unSelectCurrentPage,
    selectItem,
    unselectItem,
    unselectAll,
    selectedCount,
    isAllSelected,
    selectUnselectPage,
    canSelect,
    toggleSelectItem,
  };
  const Provider: ProviderType = useMemo(() => {
    return function ({ children }: { children: ReactNode }) {
      if (!Provider.api) return <></>;

      return (
        <SelectItemsContext.Provider value={Provider.api}>
          {children}
        </SelectItemsContext.Provider>
      );
    };
  }, []);

  Provider.api = API;
  return { ...API, Provider };
}
